import * as React from 'react';

import {
  LearnerObject_AggregatedAttributeSource as AggregatedAttributeSource,
  LearnerObject_AggregatedAttributeConfidenceClassification as ConfidenceClassification,
} from '__generated__/graphql-types';

import { useTracker } from '@coursera/event-pulse/react';

import type { InferredDesiredRole } from 'bundles/personalization-settings/hooks/useAggregatedDesiredRoles';

/** Shared `form` entity for every eventing v3 event fired from this page. */
export const PERSONALIZATION_SETTINGS_FORM = { name: 'personalization_settings' as const };

type PersonalizationButtonName =
  | 'personalization_settings_add_skill'
  | 'personalization_settings_remove_skill'
  | 'personalization_settings_add_desired_role'
  | 'personalization_settings_remove_desired_role'
  | 'personalization_settings_confirm_add_current_role';

/** Modal names this page can show -- currently just the add-current-role prompt. */
type PersonalizationModalName = 'personalization_settings_add_current_role';

type PersonalizationSelectName = 'learning_goal' | 'education_level' | 'current_role' | 'skills' | 'desired_roles';

type PersonalizationTextInputName = 'skills_search' | 'desired_roles_search' | 'current_role_search';

/** Returns a function firing `click_button` for one of this page's add/remove chip buttons. */
export const useTrackPersonalizationButtonClick = () => {
  const track = useTracker();
  return React.useCallback<(name: PersonalizationButtonName) => void>(
    (name) => track('click_button', { form: PERSONALIZATION_SETTINGS_FORM, button: { name } }),
    [track]
  );
};

/**
 * Returns a function firing `change_select` for one of this page's fields.
 * `value` is the full resulting selection (all currently selected items),
 * not just the added/removed item.
 */
export const useTrackPersonalizationSelectChange = () => {
  const track = useTracker();
  return React.useCallback<(name: PersonalizationSelectName, value: string[]) => void>(
    (name, value) => track('change_select', { form: PERSONALIZATION_SETTINGS_FORM, select: { name, value } }),
    [track]
  );
};

/** Returns a function firing `view_modal` for one of this page's modals. */
export const useTrackPersonalizationModalView = () => {
  const track = useTracker();
  return React.useCallback<(name: PersonalizationModalName) => void>(
    (name) => track('view_modal', { form: PERSONALIZATION_SETTINGS_FORM, modal: { name } }),
    [track]
  );
};

/** Returns a function firing `close_modal` for one of this page's modals. */
export const useTrackPersonalizationModalClose = () => {
  const track = useTracker();
  return React.useCallback<(name: PersonalizationModalName) => void>(
    (name) => track('close_modal', { form: PERSONALIZATION_SETTINGS_FORM, modal: { name } }),
    [track]
  );
};

/** Debounce applied to typeahead input before a `change_text_input` event fires (ms). */
const SEARCH_TRACKING_DEBOUNCE_MS = 500;

/**
 * Returns a function to call from a typeahead's own input-change handler
 * (never from an effect resyncing `input` to an externally-changing value --
 * that would misfire this as a "search" whenever the value changes for any
 * other reason). Fires `change_text_input` after `value` settles for
 * {@link SEARCH_TRACKING_DEBOUNCE_MS}; clearing the field is not tracked.
 */
export const useDebouncedPersonalizationSearchTracker = (name: PersonalizationTextInputName) => {
  const track = useTracker();
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return React.useCallback<(value: string) => void>(
    (value: string) => {
      clearTimeout(timeoutRef.current);
      const trimmedValue = value.trim();
      if (!trimmedValue) return;
      timeoutRef.current = setTimeout(() => {
        track('change_text_input', {
          form: PERSONALIZATION_SETTINGS_FORM,
          textInput: { name, value: trimmedValue },
        });
      }, SEARCH_TRACKING_DEBOUNCE_MS);
    },
    [name, track]
  );
};

/** Button names for an inferred desired role: its impression, then each review action. */
export type InferredDesiredRoleButtonName =
  | 'personalization_settings_inferred_desired_role'
  | 'personalization_settings_confirm_inferred_desired_role'
  | 'personalization_settings_reject_inferred_desired_role'
  | 'personalization_settings_undo_reject_inferred_desired_role';

type InferredDesiredRoleClickName = Exclude<
  InferredDesiredRoleButtonName,
  'personalization_settings_inferred_desired_role'
>;

const LEARNER_ATTRIBUTE_SOURCE = {
  [AggregatedAttributeSource.Explicit]: 'explicit',
  [AggregatedAttributeSource.CoachMemoryInferred]: 'coach_memory_inferred',
  [AggregatedAttributeSource.MlInferred]: 'ml_inferred',
} as const;

const LEARNER_ATTRIBUTE_CONFIDENCE = {
  [ConfidenceClassification.High]: 'high',
  [ConfidenceClassification.Medium]: 'medium',
  [ConfidenceClassification.Low]: 'low',
} as const;

/**
 * Event data shared by an inferred role's impression and its review clicks. The one place GraphQL
 * enums are mapped to the eventing schema's; an unclassified role omits its bucket, since the
 * schema has no null bucket.
 */
export const inferredDesiredRoleEventData = (name: InferredDesiredRoleButtonName, role: InferredDesiredRole) => ({
  form: PERSONALIZATION_SETTINGS_FORM,
  button: { name },
  learnerAttribute: {
    attributeKey: 'interested_careers' as const,
    source: LEARNER_ATTRIBUTE_SOURCE[role.source],
    valueId: role.id,
    ...(role.confidenceClassification
      ? { confidenceClassification: LEARNER_ATTRIBUTE_CONFIDENCE[role.confidenceClassification] }
      : {}),
  },
});

/** Returns a function firing `click_button` for a review action on an inferred desired role. */
export const useTrackInferredDesiredRoleClick = () => {
  const track = useTracker();
  return React.useCallback<(name: InferredDesiredRoleClickName, role: InferredDesiredRole) => void>(
    (name, role) => track('click_button', inferredDesiredRoleEventData(name, role)),
    [track]
  );
};
